import { logger } from '@fe/core';
import { WorkspaceWindowPanelType } from '../../app/components/multi-window/types';
import {
    NewRouteModel,
    remoteAnalyzerRemoveAllRoutes,
    routeAnalyzerAddRoutes,
    routeAnalyzerUpdateRoute
} from '../../app/state/slices/route-analyzer-slice';
import { setWorkspacePanelVisibility } from '../../app/state/slices/workspace-slice';
import { store } from '../../app/state/store';
import { getWorker } from '../../client/worker-container';
import constants from '../../common/constants';
import { FPMMessageType } from '../../common/enums';
import {
    AnalyzerRoute,
    AnalyzerRouteType,
    MAX_VISIBLE_ROUTE_COUNT,
    ROUTE_ANALYZER_DEFAULT_VERIFY_COLOR
} from '../../components/route-analyzer/types';
import { FPMPlannedRouteResponse, FPMRouteFlags, FPMRouteResponse, FPMVerifyRouteResponse } from '../../messages/message-parser';
import { MessageType } from '../actor-messages';
import { FpmRequestKind, fpmRequestTracker, getIntegrationName } from './fpm-request-tracker';

interface ReceivedRoute {
    route: NewRouteModel;
    acid: string | null;
}

// Imports routes sent by the FPM / desktop integration (RouteGet responses, RoutePush, Calculate and Verify results)
// into the route analyzer. Lives outside of React so routes are never lost, regardless of the page the user is on.
class FpmRouteImporter {
    private received: ReceivedRoute[] = [];
    private readonly pendingPlannedInfo = new Map<string, FPMPlannedRouteResponse>();
    private readonly importedRouteKeys = new Set<string>();

    handleRouteResponse(message: FPMRouteResponse) {
        // first message - clear routes and reset tracking
        if (message.cFlags & FPMRouteFlags.clearList) {
            store.dispatch(remoteAnalyzerRemoveAllRoutes());
            this.received = [];
            this.pendingPlannedInfo.clear();
            this.importedRouteKeys.clear();
        }

        const labelSuffix = message.wVarOrRelNumber ? `-${message.wVarOrRelNumber}` : '';
        this.received.push({
            acid: message.pczACID || null,
            route: newRoute({
                routeKey: createRouteKey(message.pczRouteID, message.wVarOrRelNumber),
                type: message.cRouteType as AnalyzerRouteType,
                label: message.pczACID ? `${message.pczACID}-${message.pczRouteID}${labelSuffix}` : message.pczRouteID,
                origin: message.pczOrigin || message.pcRoute.split(/[ .]/, 1)[0],
                destination: message.pczDest || message.pcRoute.split(/[ .]/).slice(-1)[0],
                sequence: 'FPM',
                route: message.pcRoute,
                visible: !!(message.cFlags & FPMRouteFlags.draw)
            })
        });

        // last record - add the routes and open the route analyzer
        if (message.cFlags & FPMRouteFlags.lastRoute) {
            const received = this.received;
            this.received = [];
            const kind = message.cSubCode === FPMMessageType.CalculateRouteRs ? FpmRequestKind.calculate : FpmRequestKind.routes;
            this.importRoutes(received, kind).catch((err) => {
                logger.trackException(err);
                fpmRequestTracker.emptyResult(kind, `Could not add routes from ${getIntegrationName()} to Route Analyzer`);
            });
        }
    }

    handlePlannedRoute(message: FPMPlannedRouteResponse) {
        if (message.psFixRecs.length === 0) {
            return;
        }

        const routeKey = createRouteKey(message.pczRouteID, message.wVarOrRelNumber);
        this.pendingPlannedInfo.set(routeKey, message);

        // If the route was already imported, apply the info immediately, otherwise it's applied once it's imported
        if (this.importedRouteKeys.has(routeKey)) {
            this.applyPlannedInfo(routeKey);
        }
    }

    handleVerifyResponse(message: FPMVerifyRouteResponse) {
        if (message.pcRoute.length === 0) {
            fpmRequestTracker.emptyResult(FpmRequestKind.verify, `${getIntegrationName()} returned an empty verified route`);
            return;
        }

        const route = newRoute({
            type: AnalyzerRouteType.other,
            label: message.pczRouteID + '-Verified',
            origin: message.pcRoute.split(/[ .]/)[0],
            destination: message.pcRoute.split(/[ .]/).slice(-1)[0],
            sequence: 'FPM',
            color: ROUTE_ANALYZER_DEFAULT_VERIFY_COLOR,
            route: message.pcRoute,
            visible: this.visibleSlotsLeft() > 0
        });

        store
            .dispatch(routeAnalyzerAddRoutes({ routes: [route] }))
            .then(() => {
                this.openRouteAnalyzer();
                fpmRequestTracker.routesReceived(FpmRequestKind.verify, 1);
            })
            .catch((err) => logger.trackException(err));
    }

    private async importRoutes(received: ReceivedRoute[], kind: FpmRequestKind.routes | FpmRequestKind.calculate) {
        await this.linkFlights(received);

        let visibleSlots = this.visibleSlotsLeft();
        const routes = received.map(({ route }) => {
            const visible = route.visible && visibleSlots > 0;
            if (visible) {
                visibleSlots--;
            }
            return { ...route, visible };
        });

        await store.dispatch(routeAnalyzerAddRoutes({ routes }));

        for (const route of routes) {
            if (route.routeKey) {
                this.importedRouteKeys.add(route.routeKey);
                this.applyPlannedInfo(route.routeKey);
            }
        }

        this.openRouteAnalyzer();
        fpmRequestTracker.routesReceived(kind, routes.length);
    }

    // Link routes to known flights so they can be calculated / verified later
    private async linkFlights(received: ReceivedRoute[]) {
        const flightIds = new Map<string, number | null>();
        for (const item of received) {
            if (!item.acid) {
                continue;
            }
            const key = `${item.acid}|${item.route.origin}`;
            if (!flightIds.has(key)) {
                try {
                    flightIds.set(
                        key,
                        await getWorker().actor.sendAsync({
                            type: MessageType.findFlightIdByAcid,
                            data: { acid: item.acid, origin: item.route.origin || null }
                        })
                    );
                } catch (err) {
                    logger.trackException(err);
                    flightIds.set(key, null);
                }
            }
            const flightId = flightIds.get(key);
            if (flightId != null) {
                item.route.flightId = flightId;
            }
        }
    }

    private visibleSlotsLeft() {
        const visibleCount = store.getState().routeAnalyzer.routes.filter((x) => x.visible).length;
        return Math.max(0, MAX_VISIBLE_ROUTE_COUNT - visibleCount);
    }

    private openRouteAnalyzer() {
        const state = store.getState();
        const inExternalWindow = state.workspace.windows.some((x) =>
            x.panels.some((x) => x.type === WorkspaceWindowPanelType.routeAnalyzer)
        );
        if (!inExternalWindow && !state.workspace.panels.routeAnalyzer) {
            store.dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: true }));
        }
    }

    private applyPlannedInfo(routeKey: string) {
        const plannedInfo = this.pendingPlannedInfo.get(routeKey);
        if (!plannedInfo) {
            return;
        }

        const routes = store.getState().routeAnalyzer.routes.filter((x) => x.routeKey === routeKey);
        for (const route of routes) {
            if (!route.waypoints) {
                continue;
            }

            const modifiedRoute: AnalyzerRoute = {
                ...route,
                waypoints: {
                    ...route.waypoints,
                    items: route.waypoints.items.map((waypoint) => {
                        const fix = plannedInfo.psFixRecs.find((f) => f.pcID === waypoint.id);
                        return fix
                            ? {
                                  ...waypoint,
                                  fob: fix.dwFuelOnBoard ?? undefined,
                                  at: fix.wCruiseAlt ?? undefined,
                                  // fewp = seconds since Jan 1, 1990 00:00:00 (they almost got the date right)
                                  ete: fix.dwFEWPTimeOver + constants.feEpoch
                              }
                            : waypoint;
                    })
                }
            };
            store.dispatch(routeAnalyzerUpdateRoute({ route: modifiedRoute }));
        }

        this.pendingPlannedInfo.delete(routeKey);
    }
}

function createRouteKey(routeId: string, varNumber: number | null) {
    return varNumber ? `${routeId}-${varNumber}` : routeId;
}

function newRoute(patch: Partial<NewRouteModel>): NewRouteModel {
    return {
        type: AnalyzerRouteType.adhoc,
        label: '',
        origin: '',
        destination: '',
        sequence: '',
        distance: null,
        ete: null,
        time: null,
        route: '',
        waypoints: null,
        associatedPoints: [],
        feedId: null,
        visible: false,
        ...patch
    };
}

export const fpmRouteImporter = new FpmRouteImporter();
